"use client";

import React, { useState, useEffect } from "react";

interface BasicInputsProps {
    loanAmount: number;
    setLoanAmount: (value: number) => void;
    loanTerm: number;
    setLoanTerm: (value: number) => void;
    mortgageRate: number;
    setMortgageRate: (value: number) => void;
}

const formatWithCommas = (value: number | string): string => value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");

const BasicInputs: React.FC<BasicInputsProps> = ({
    loanAmount,
    setLoanAmount,
    loanTerm,
    setLoanTerm,
    mortgageRate,
    setMortgageRate,
}) => {
    const [loanAmountInput, setLoanAmountInput] = useState("0");
    const [isOpen, setIsOpen] = useState(true);

    useEffect(() => {
        setLoanAmountInput(formatWithCommas(loanAmount));
    }, [loanAmount]);

    useEffect(() => {
        setLoanAmountInput(formatWithCommas(loanAmount));
    }, [loanAmount]);

    return (
        <div className="border border-gray-300 overflow-hidden shadow 2xl:w-[900px]">
            {/* Clickable Header */}
            <button
                type="button"
                className="w-full text-left bg-[#202358] text-white px-4 py-3 cursor-pointer select-none flex items-center justify-between hover:bg-blue active:bg-blue focus:bg-blue 2xl:h-[42px]"
                onClick={() => setIsOpen(!isOpen)}
            >
                <h5 className="text-lg font-bold">BASIC</h5>
                <span className="text-xl">{isOpen ? "−" : "+"}</span>
            </button>

            {/* Animated Expand/Collapse Content */}
            <div
                className={`transition-all duration-300 ease-in-out overflow-hidden ${
                    isOpen ? "max-h-[1000px] opacity-100" : "max-h-0 opacity-0"
                }`}
            >
                <div className="bg-white p-3 2xl:w-[898px]">
                    {/* Loan Amount Row */}
                    <div>
                        <div className="flex flex-col gap-3 2xl:flex 2xl:flex-row 2xl:items-center 2xl:gap-4 ">
                            <div className="2xl:w-[367px] 2xl:flex 2xl:justify-end">
                                <label
                                    htmlFor="loanAmount"
                                    className="w-32 font-medium text-black text-[15px] 2xl:w-[106px] content-center"
                                >
                                    Loan Amount: $
                                </label>
                                <input
                                    id="loanAmount"
                                    type="text"
                                    inputMode="numeric"
                                    className="border px-3 py-1 rounded w-36 text-left 2xl:w-[122px] bg-[#f6f9ff] text-[14px]"
                                    value={loanAmountInput}
                                    onChange={(e) => {
                                        const raw = e.target.value.replace(/,/g, "");
                                        if (/^\d*$/.test(raw)) {
                                            const numeric = Number(raw);
                                            setLoanAmount(numeric);
                                            setLoanAmountInput(formatWithCommas(raw));
                                        }
                                    }}
                                    onBlur={() => {
                                        setLoanAmountInput(formatWithCommas(loanAmount));
                                    }}
                                />
                            </div>
                            <input
                                aria-label="Loan amount"
                                type="range"
                                min={0}
                                max={10000000}
                                step={1000}
                                value={loanAmount}
                                onChange={(e) => {
                                    const val = Number(e.target.value);
                                    setLoanAmount(val);
                                    setLoanAmountInput(formatWithCommas(val));
                                }}
                                className="w-full 2xl:w-[506px] accent-blue"
                            />
                        </div>
                        <div className="flex justify-end text-xs text-black mt-1">
                            <div className="pb-10 flex justify-between w-full max-w-md 2xl:pb-0 ">
                                <span>$0</span>
                                <span>$100K</span>
                                <span>$1M</span>
                                <span>$10M</span>
                            </div>
                        </div>
                    </div>

                    {/* Term Row */}
                    <div>
                        <div className="flex flex-col gap-3 2xl:flex 2xl:flex-row 2xl:items-center 2xl:gap-4">
                            <div className="2xl:w-[367px] 2xl:flex 2xl:justify-end">
                                <label
                                    htmlFor="loanTerm"
                                    className="w-32 font-medium text-black text-[15px] 2xl:w-[90px]"
                                >
                                    Term (Years):
                                </label>
                                <input
                                    id="loanTerm"
                                    type="number"
                                    className="border px-3 py-1 rounded w-36 text-left 2xl:w-[122px] bg-[#f6f9ff] text-[14px]"
                                    value={loanTerm}
                                    onChange={(e) => setLoanTerm(Number(e.target.value))}
                                />
                            </div>
                            <input
                                aria-label="Loan term in years"
                                type="range"
                                min={0}
                                max={40}
                                step={1}
                                value={loanTerm}
                                onChange={(e) => setLoanTerm(Number(e.target.value))}
                                className="w-full 2xl:w-[506px] accent-blue"
                            />
                        </div>
                        <div className="flex justify-end text-xs text-black mt-1">
                            <div className="pb-10 flex justify-between w-full max-w-md 2xl:pb-0">
                                <span>0</span>
                                <span>13</span>
                                <span>26</span>
                                <span>40</span>
                            </div>
                        </div>
                    </div>

                    {/* Interest Rate Row */}
                    <div>
                        <div className="flex flex-col gap-3 2xl:flex 2xl:flex-row 2xl:items-center 2xl:gap-4">
                            <div className="2xl:w-[367px] 2xl:flex 2xl:justify-end">
                                <label
                                    htmlFor="mortgageRate"
                                    className="w-32 font-medium text-black text-[15px] 2xl:w-[106px] content-center"
                                >
                                    Interest Rate: %
                                </label>
                                <input
                                    id="mortgageRate"
                                    type="number"
                                    className="border px-3 py-1 rounded w-36 text-left 2xl:w-[122px] bg-[#f6f9ff] text-[14px]"
                                    value={mortgageRate}
                                    onChange={(e) => setMortgageRate(Number(e.target.value))}
                                />
                            </div>
                            <input
                                aria-label="Mortgage interest rate"
                                type="range"
                                min={0}
                                max={32}
                                step={1}
                                value={mortgageRate}
                                onChange={(e) => setMortgageRate(Number(e.target.value))}
                                className="w-full 2xl:w-[506px] accent-blue"
                            />
                        </div>
                        <div className="flex justify-end text-xs text-black mt-1">
                            <div className="pb-10 flex justify-between w-full max-w-md 2xl:pb-0">
                                <span>0%</span>
                                <span>12%</span>
                                <span>24%</span>
                                <span>32%</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default BasicInputs;
